import { useEffect, useRef } from 'react';

/**
 * Guarda de recarregamento por comparação com o estado anterior (at-43).
 *
 * Substitui o efeito copiado nas telas de Recepção, Chefe de Oficina e Controle de
 * Liberação: compara filtros (por JSON), página, limite e ordenação com os valores da
 * última execução (refs) e só dispara o recarregamento quando algo mudou de fato — o
 * que impede que um simples re-render refaça a busca. A decisão de COMO recarregar
 * (busca por texto × filtros × listagem simples) continua na tela, via `onReload`.
 *
 * Os callbacks são lidos através de uma ref atualizada a cada render (padrão
 * callback-ref): o efeito depende só dos valores comparados, com dependências reais —
 * é o que elimina os eslint-disable que este padrão carregava desde a at-39.
 */
type GuardedReloadInfo = {
  isFirst: boolean;
  hasFiltersChanged: boolean;
  hasPageChanged: boolean;
  hasLimitChanged: boolean;
  hasSortChanged: boolean;
};

type UseGuardedReloadArgs<F> = {
  filters: F;
  page: number;
  limit: number;
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc' | null;
  /** Termo de busca ativo — mudanças nele NÃO disparam por si (comportamento original). */
  searchQuery?: string;
  /** Chamado quando algo mudou (ou na primeira execução). */
  onReload: (info: GuardedReloadInfo) => void;
  /** Chamado quando nada mudou (as telas usam para desligar o loading). */
  onSkip?: () => void;
};

export function useGuardedReload<F>({
  filters,
  page,
  limit,
  sortBy,
  sortOrder,
  searchQuery,
  onReload,
  onSkip,
}: UseGuardedReloadArgs<F>) {
  const prevFilters = useRef<F>(filters);
  const prevPage = useRef(page);
  const prevLimit = useRef(limit);
  const prevSortBy = useRef(sortBy);
  const prevSortOrder = useRef(sortOrder);
  const isFirstRender = useRef(true);

  const callbacksRef = useRef({ onReload, onSkip });
  callbacksRef.current = { onReload, onSkip };

  useEffect(() => {
    const hasFiltersChanged = JSON.stringify(filters) !== JSON.stringify(prevFilters.current);
    const hasPageChanged = page !== prevPage.current;
    const hasLimitChanged = limit !== prevLimit.current;
    const hasSortChanged = sortBy !== prevSortBy.current || sortOrder !== prevSortOrder.current;
    const isFirst = isFirstRender.current;

    if (isFirst || hasFiltersChanged || hasPageChanged || hasLimitChanged || hasSortChanged) {
      isFirstRender.current = false;

      prevFilters.current = filters;
      prevPage.current = page;
      prevLimit.current = limit;
      prevSortBy.current = sortBy;
      prevSortOrder.current = sortOrder;

      callbacksRef.current.onReload({
        isFirst,
        hasFiltersChanged,
        hasPageChanged,
        hasLimitChanged,
        hasSortChanged,
      });
    } else {
      callbacksRef.current.onSkip?.();
    }
    // `searchQuery` entra nas dependências (comportamento original: o efeito re-executa
    // quando o termo muda, mas a mudança dele sozinha cai no onSkip — quem dispara a
    // busca é a página/filtro/ordenação, ou o submit da própria tela).
  }, [filters, page, limit, sortBy, sortOrder, searchQuery]);
}
